import qs from 'query-string'

// adds a buffer of a few seconds so we refresh an
// access token early
const BUFFER = 10

// root url for netlify functions
const ROOT_URL = '/.netlify/functions'

// local storage keys
const NAME = 'name'
const ACCESS_TOKEN = 'access_token'
const REFRESH_TOKEN = 'refresh_token'
const EXPIRES_AT = 'expires_at'
const LOGGED_IN = 'logged_in'

/**
 * Handles authentication for the API explorer,
 * storing the info in local storage for client-side usage
 */
class AccessToken {
  /**
   * Creates a new AcessToken object to keep track of the
   * token and associated info
   */
  constructor({
    credentials,
    setCredentials,
    customClientName,
    customClientId,
    customClientSecret
  }) {
    this.credentials = credentials
    this.setCredentials = setCredentials
    this.customClientName = customClientName
    this.customClientId = customClientId
    this.customClientSecret = customClientSecret
  }

  /**
   * Returns either the cached access token or
   * automatically  exchanges it for a new one if 
   * the token has expired
   */
  async token() {
    // get a new token if necessary
    await this.refreshWhenExpired()
    // finally, return the access token
    return this.get(ACCESS_TOKEN)
  }

  /**
   * Exchange the authorization code for an access token
   * and store it in local storage
   */
  async exchangeCode(location) {
    // extract the code from the query params
    const code = qs.parse(location.search).code

    // exchange the code
    const response = await fetch(`${ROOT_URL}/exchange-code`, {
      method: 'POST',
      body: JSON.stringify({
        code: code,
        customClientId: this.customClientId,
        customClientSecret: this.customClientSecret
      })
    })
    if (response.status !== 200) { return this.logout() }

    // extract all important info from the response
    const data = await response.json()

    // store the response
    this.set({
      name: data.profile.name,
      login: data.profile.login,
      user_id: data.profile.id,
      enterprise_id: data.profile.enterprise.id,
      access_token: data.accessToken,
      refresh_token: data.refreshToken,
      expires_at: data.expiresAt,
      logged_in: data.loggedIn
    })
  }

  /**
   * Logs out a user
   */
  logout() {
    this.remove(NAME)
    this.remove(ACCESS_TOKEN)
    this.remove(REFRESH_TOKEN)
    this.remove(EXPIRES_AT)
    this.remove(LOGGED_IN)
  }

  /**
   * Returns the current logged in status
   */
  loggedIn() {
    return this.get(LOGGED_IN)
  }

  /**
   * Returns the current logged in status
   */
  refresh() {
    return this.set({ refresh: Date.now() })
  }

  /**
   * Returns the username
   */
  username() {
    return this.get(NAME)
  }

  // PRIVATE

  /**
   * Sets a local storage entry
   */
  set(newCredentials) {
    this.setCredentials(credentials => ({
      ...credentials, ...newCredentials
    }))
  }

  /** 
   * Gets a local storage value
   */
  get(key) {
    return this.credentials[key]
  }

  /**
   * Removes a local storage value
   */
  remove(key) {
    this.setCredentials(credentials => {
      let newCredentials = { ...credentials }
      delete newCredentials[key]
      return newCredentials
    })
  } 

  /**
   * Exchanges a refresh token for a new access token
   */
  async exchangeRefreshToken() {
    const refreshToken = this.get(REFRESH_TOKEN)
    const response = await fetch(`${ROOT_URL}/exchange-refresh-token?token=${refreshToken}`)
    if (response.status !== 200) { return this.logout() }

    // extract all important info from the response
    const data = await response.json()

    // store the response
    this.set({
      access_token: data.accessToken,
      refresh_token: data.refreshToken,
      expires_at: data.expiresAt,
      logged_in: data.loggedIn
    })
  }

  /**
   * Refreshes the access token if it has expired
   */
  async refreshWhenExpired() {
    // check the expiry date
    const expiresAt = new Date(parseInt(this.get(EXPIRES_AT)))
    // check if the access token has expired
    const expired = expiresAt < Date.now()+BUFFER
    // if expired, exchnage the refresh token for a new
    // access token
    if (expired) { await this.exchangeRefreshToken() } 
  }
}

export default AccessToken